import React, { ReactElement, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';
import { ActionCreatorWithOptionalPayload } from '@reduxjs/toolkit';
import { Table } from '../../../../../../components';
import { NetworkOrVlansCellRenderer } from '../../../components/VirtualMachineEntities/Edit/components/Networking/components/NetworkOrVlansCellRenderer';
import { NetworkTableActions } from './NetworkTableActions';

interface NetworkTableProps {
  networkInterfaces: HardwareNetworkInterface[];
  networks: Network[];
  onDeleteClick: (id: string) => void;
  setViewAllNetworksModal: ActionCreatorWithOptionalPayload<
    string[] | undefined,
    string
  >;
}

enum TableTranslations {
  Interface = 'interface',
}

const translationKey = (key: TableTranslations) =>
  `hardware.edit.networkingInterface.table.${key}`;

export function NetworkTable({
  networkInterfaces,
  networks,
  onDeleteClick,
  setViewAllNetworksModal,
}: NetworkTableProps): ReactElement {
  const { t } = useTranslation();

  const columns = useMemo<Column<HardwareNetworkInterface>[]>(
    () => [
      {
        Cell: ({ value }) => value.id,
        Header: () => t(translationKey(TableTranslations.Interface)),
        accessor: 'networkInterface',
      },
      {
        Cell: ({ row }) => (
          <NetworkOrVlansCellRenderer
            nic={row.original}
            networks={networks}
            setViewAllNetworksModal={setViewAllNetworksModal}
          />
        ),
        Header: () => t('networks.trunk.networkOrVlans'),
        id: 'networkOrVlans',
      },
      {
        Cell: ({ row }) =>
          row.original.trunk?.enabled ? <span>&#x2713;</span> : <></>,
        Header: () => t('networks.trunk.label'),
        id: 'trunk',
      },
    ],
    [t, networks, setViewAllNetworksModal],
  );

  const renderActions = (
    networkInterface: HardwareNetworkInterface,
  ): ReactNode => {
    return (
      <NetworkTableActions
        onDeleteClick={() => onDeleteClick(networkInterface.uid)}
      />
    );
  };

  return (
    <div className="dbl-margin-top">
      <Table<HardwareNetworkInterface>
        actions={renderActions}
        data={networkInterfaces}
        columns={columns}
      />
    </div>
  );
}
